/*
  Plain facts from a JSON value, for the "before and after" of a Next version and for the lines of a card.
  The contracts give `before` and `after` as free JSON, so this reads any shape: nested objects become
  "Unit › Symbol", a list of plain values becomes one row, and an id of another element is shown as a reference.
*/

import { humanise } from "../shared/words";

export interface FactRow {
  path: string;
  label: string;
  value: string;
  /** The id, when the value points at another element. */
  reference?: string;
}

export type Change = "same" | "changed" | "added" | "removed";

export interface DiffRow {
  path: string;
  label: string;
  before?: string;
  after?: string;
  change: Change;
}

/** Keys that hold the loaded definition or a catch-all, not something a person reads. */
const HIDDEN_KEYS = new Set(["linkml_extra", "raw"]);

const ID_PATTERN = /^[a-z]{2}_[0-9a-z]{26}$/;

export function isId(value: unknown): value is string {
  return typeof value === "string" && ID_PATTERN.test(value);
}

function isPlain(value: unknown): value is string | number | boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

function plainText(value: string | number | boolean): string {
  if (typeof value === "boolean") return value ? "yes" : "no";
  return String(value);
}

function labelOf(path: string[]): string {
  return path.map((part) => (/^#\d+$/.test(part) ? part : humanise(part))).join(" › ");
}

function walk(value: unknown, path: string[], out: FactRow[]): void {
  if (value === null || value === undefined) return;
  if (isPlain(value)) {
    if (typeof value === "string" && value === "") return;
    const reference = isId(value) ? value : undefined;
    out.push({ path: path.join("."), label: labelOf(path), value: reference ? "another element" : plainText(value), reference });
    return;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return;
    if (value.every((item) => isPlain(item))) {
      const items = value as Array<string | number | boolean>;
      out.push({
        path: path.join("."),
        label: labelOf(path),
        value: items.map((item) => (isId(item) ? "another element" : plainText(item))).join(", "),
        reference: items.some((item) => isId(item)) ? items.filter(isId).join(" ") : undefined,
      });
      return;
    }
    value.forEach((item, index) => walk(item, [...path, `#${index + 1}`], out));
    return;
  }
  if (typeof value === "object") {
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
      if (HIDDEN_KEYS.has(key)) continue;
      walk(inner, [...path, key], out);
    }
  }
}

/** The facts of a JSON value, in the order they appear. */
export function factRows(value: unknown): FactRow[] {
  const rows: FactRow[] = [];
  walk(value, [], rows);
  return rows;
}

/** Before and after side by side: every fact of either side, marked changed, added, removed or the same. */
export function diffFacts(before: unknown, after: unknown): DiffRow[] {
  const left = new Map(factRows(before).map((row) => [row.path, row]));
  const right = new Map(factRows(after).map((row) => [row.path, row]));
  const order: string[] = [];
  for (const key of right.keys()) order.push(key);
  for (const key of left.keys()) if (!right.has(key)) order.push(key);
  return order.map((path) => {
    const a = left.get(path);
    const b = right.get(path);
    const label = (b ?? a)!.label;
    if (a && b) return { path, label, before: a.value, after: b.value, change: a.value === b.value && a.reference === b.reference ? "same" : "changed" };
    if (b) return { path, label, after: b.value, change: "added" };
    return { path, label, before: a!.value, change: "removed" };
  });
}

export function changedCount(rows: readonly DiffRow[]): number {
  return rows.filter((row) => row.change !== "same").length;
}
